import { Chart } from 'chart.js';
import Moment from 'moment-timezone';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import XLSX from 'xlsx';
import { evaluate } from 'mathjs';
import { i18n } from '@/plugins/i18n';
import store from '@/store';
import {
  decodeHTML,
  getTextWidth,
  isWorkingDay,
  parseWorkingDays,
} from '@/utils/main';
import { SColors } from '@/utils/constants';
// eslint-disable-next-line no-unused-vars
import { warningMedium } from '@/assets/icons';
import { utilsTiptapEditor } from '@/plugins/tiptap';
import { setColumnSize, convertAOAToTable } from '@/utils/sheets';

/**
 * Values object is formatted as { <date>: <valueObject>, <date2>: <value2Object>, ...}
 * This is the date format of the key
 */
export const valueKeyFormat = 'YYYY-MM-DD';


/**
 * Create a Pareto Chart from an array of indicators. Each indicator belongs to a specific team
 * The Pareto chart displays how the teams exceed their objectives over a specific period
 *
 * @param  {HTMLElement}  canvas - Element to build the Chart
 * @param  {Array}  indicators - array of indicators
 * @param  {Object}  range - object with a 'cStart' and 'cEnd' props corresponding to the bounds of the period
 * @return {Chart} Chart instance
 */
export function buildParetoAnalysisChart(canvas, indicators, range) {
  let startDate;
  let diff;
  let formattedValues;
  let valObject;
  let totalValues;
  let totalObjectives;
  let workThisDay;
  let days;
  let indicator;
  let nDays;
  const chartBars = [];
  let paretoMax = 0;
  let maxDiff = 0;
  // Objective is 0 => any value divided by 0 would mean a discrepancy of 100%
  // so it doesn't make sense to the users => show values instead
  const valuesMode = indicators.find(indic => Number(indic.objective) === 0);
  const percentageSuffix = valuesMode ? '' : '%';
  // Calculate averag gaps to objective
  for (let i = 0, len = indicators.length; i < len; i++) {
    startDate = range.cStart.clone();
    indicator = indicators[i];
    // eslint-disable-next-line no-multi-assign
    nDays = diff = totalValues = totalObjectives = 0;
    workThisDay = [false, false, false, false, false, false, false, false];
    formattedValues = indicator.inputValues;
    days = JSON.parse(indicator.workingDays);
    for (let j = 0, jLen = days.length; j < jLen; j++) {
      workThisDay[days[j] + 1] = true;
    }
    // Loop through all the values and calculate the average gap between the indicator values and its objective
    while (startDate.add(1, 'd').diff(range.cEnd) <= 0) {
      if (workThisDay[startDate.isoWeekday()]) {
        nDays++;
        valObject = formattedValues[startDate.format(valueKeyFormat)];
        totalValues += valObject ? Number(valObject.value || 0) : 0;
        totalObjectives += Number(indicator.objectives[startDate.format(valueKeyFormat)]);
      }
    }
    if (valuesMode) {
      diff = -totalValues / nDays;
    } else {
      diff = (totalObjectives - totalValues) / totalObjectives;
      diff = Number((100 * diff / nDays).toFixed(2));
    }
    if (Number(indicator.objectiveIsMax) === 1) {
      diff = -diff; // So a positive value means 'bad'
    }
    chartBars.push({ diff, teamName: indicator.teamName });
    maxDiff = maxDiff > Math.abs(diff) ? maxDiff : Math.abs(diff);
    if (diff > 0) {
      paretoMax += diff;
    }
  }
  // Calculate Pareto line
  chartBars.sort((a, b) => b.diff - a.diff);
  const chartLabels = [];
  const chartValues = [];
  const chartLine = []; // Pareto line values
  for (let i = 0; i < chartBars.length; i++) {
    chartLabels.push(chartBars[i].teamName);
    chartValues.push(chartBars[i].diff);
  }
  let prevValue = 0;
  for (let i = 0; i < chartValues.length; i++) {
    if (chartValues[i] > 0) {
      prevValue += chartValues[i] * 100 / paretoMax;
      chartLine.push(prevValue.toFixed(2));
    } else { // Reached the 'ok' values, pareto must reach its top
      chartLine.push(100);
    }
  }
  paretoMax = paretoMax === 0 ? maxDiff * 1.1 : paretoMax;
  paretoMax = Number.isInteger(paretoMax) ? paretoMax : paretoMax.toFixed(2);
  // Make Pareto chart with gaps to objective as bars and gaps accumulation as line
  canvas.width = canvas.offsetWidth;
  canvas.height = canvas.offsetHeight;
  return new Chart(canvas.getContext('2d'), {
    type: 'bar',
    data: {
      labels: chartLabels,
      datasets: [{
        type: 'line',
        backgroundColor: 'transparent',
        borderColor: 'rgb(197,107,0)',
        tension: 0,
        data: chartLine,
        borderWidth: 2,
        yAxisID: 'yRight',
      },
      {
        backgroundColor: 'rgba(0,173,238,0.3)',
        borderColor: 'rgb(0,173,238)',
        data: chartValues,
        borderWidth: 2,
        yAxisID: 'yLeft',
      }],
    },
    options: {
      animation: { duration: 0 },
      scales: {
        yRight: {
          min: -100,
          max: 100,
          position: 'right',
          ticks: {
            callback: l => l + percentageSuffix,
          },
          grid: {
            color: 'transparent',
          },
        },
        yLeft: {
          min: -paretoMax,
          max: paretoMax,
          ticks: {
            callback: l => l + percentageSuffix,
          },
          grid: {
            drawTicks: false,
            color: ctx => (ctx.tick && ctx.tick.value === 0 ? '#58595B' : Chart.defaults.borderColor),
          },
        },
        x: {
          ticks: {
            autoSkip: false,
            padding: 5,
          },
          grid: {
            color: 'transparent',
            drawTicks: false,
          },
        },
      },
      plugins: {
        tooltip: {
          callbacks: {
            title: () => '',
            label: (tooltipItem) => {
              const val = chartValues[tooltipItem.dataIndex];
              return val > 0
                ? i18n.t('message.TEAM_AVERAGE_GAP', {
                  team: chartLabels[tooltipItem.dataIndex],
                  gap: val,
                  percStr: percentageSuffix,
                })
                : i18n.t('message.TEAM_VALUES_OK_OBJECIVE', {
                  team: chartLabels[tooltipItem.dataIndex],
                });
            },
          },
        },
      },
      layout: { padding: { left: 5, right: 10, top: 0, bottom: 0 } },
    },
  });
}

/** Return given string with a first letter capitalized */
export function getFillOption(indicator, target, user) {
  const {
    indicatorsGreenBg,
    indicatorsRedBg,
    indicatorsGreenLine,
    indicatorsRedLine,
  } = SColors;

  const objMax = Number(indicator.objectiveIsMax) === 1;
  const option = {
    target: target || 'false',
    above: 'transparent',
    below: 'transparent',
    // eslint-disable-next-line no-nested-ternary
    aboveBorder: indicator.id === 1 ? (objMax ? indicatorsRedLine : indicatorsGreenLine) : undefined,
    // eslint-disable-next-line no-nested-ternary
    belowBorder: indicator.id === 1 ? (objMax ? indicatorsGreenLine : indicatorsRedLine) : undefined,
  };

  if (Number(user.fillBadValues) === 1) {
    option[objMax ? 'above' : 'below'] = indicatorsRedBg;
  }
  if (Number(user.fillGoodValues) === 1) {
    option[objMax ? 'below' : 'above'] = indicatorsGreenBg;
  }
  return option;
}

/** Return given string with a first letter capitalized */
export function getLineColorOption(indicator, target) {
  const { indicatorsGreenLine, indicatorsRedLine } = SColors;
  const objMax = Number(indicator.objectiveIsMax) === 1;
  return {
    target: target || 'false',
    above: objMax ? indicatorsRedLine : indicatorsGreenLine,
    below: objMax ? indicatorsGreenLine : indicatorsRedLine,
  };
}

/** Make a library themes aray readable by an EditableLabelRadio */
export function mapLibraryToRadio(app, themes) {
  return themes.map(theme => ({
    id: theme.id,
    title: Number(theme.id) === 0 ? app.$t('message.LIBRARY_INDIC_UNCATEGORIZED') : theme.name,
    radioItems: theme.indicators,
  }));
}

/**
 * Shift configStart to the latest possible date in order to avoid having the following range:
 * start: 1st jan of 2016, end: 31st dec of 2025
 * Useful when using a config with increasing values => Makes sure that the chart is calculated
 * using all the values necessary before the visible range.
 * @param  {Object} config - indicator display config
 * @param  {Moment} visibleStartDate - visible start date
 * @return {Object} - Updated config
 */
export function computeConfigStart(config, visibleStartDate) {
  if (!config) {
    return config;
  }
  let increasePeriod = config.pointPeriod || config.pointPeriodObj.period;
  let increasePeriodUnit = config.pointUnit || config.pointPeriodObj.unit;
  if (config.increasingValues) {
    increasePeriod = config.increasePeriod || 1;
    increasePeriodUnit = config.increasePeriodUnit || 'M';
    const mChartEndDate = Moment(config.chartEndDate || config.chartEnd);
    config.configStart = Moment(mChartEndDate).subtract((increasePeriod - 1), increasePeriodUnit);
  }
  const mConfigStart = Moment(config.configStart).startOf(increasePeriodUnit === 'w' ? 'isoWeek' : increasePeriodUnit);
  while (mConfigStart.isAfter(visibleStartDate)) {
    mConfigStart.subtract(increasePeriod, increasePeriodUnit);
  }
  while (mConfigStart.isBefore(visibleStartDate)) {
    mConfigStart.add(increasePeriod, increasePeriodUnit);
  }
  mConfigStart.subtract(increasePeriod, increasePeriodUnit);
  return { ...config, configStart: mConfigStart };
}

export const CALC_FIELD_KEYS = [
  {
    displayedString: 'Saisie manuelle',
    displayedStringTr: 'message.MANUAL_INPUT',
    calculationKey: 'value',
    isValue: true,
  },
  {
    displayedString: 'Indicateur de mon équipe',
    displayedStringTr: 'message.INDICATOR_OF_MY_TEAM',
    calculationKey: 'indicator-value',
    isValue: true,
  },
  {
    displayedString: 'Indicateur d\'une autre équipe',
    displayedStringTr: 'message.INDICATOR_OF_OTHER_TEAM',
    calculationKey: 'indicator-value',
    isValue: true,
    pickTeam: true,
  },
];

// TODO add comma/point
export const CALC_NUMBER_KEYS = [
  {
    displayedString: '0',
  },
  {
    displayedString: '1',
  },
  {
    displayedString: '2',
  },
  {
    displayedString: '3',
  },
  {
    displayedString: '4',
  },
  {
    displayedString: '5',
  },
  {
    displayedString: '6',
  },
  {
    displayedString: '7',
  },
  {
    displayedString: '8',
  },
  {
    displayedString: '9',
  },
];

export const CALC_KEYS = [
  {
    displayedString: '+',
    spaceAround: true,
  },
  {
    displayedString: '-',
    spaceAround: true,
  },
  {
    displayedString: '×',
    calculationKey: '*',
    spaceAround: true,
  },
  {
    displayedString: '÷',
    calculationKey: '/',
    spaceAround: true,
  },
  {
    calculationKey: '.',
    displayedString: '.',
    displayedStringTr: 'message.DECIMAL_POINT',
  },
  {
    displayedString: '(',
  },
  {
    displayedString: ')',
  },
];

export const CALC_ROWS = [
  {
    items: CALC_FIELD_KEYS,
    keyClass: { blue: true, 'calc-field': true },
  },
  {
    items: CALC_KEYS,
    keyClass: { orange: true, 'calc-sign': true },
  },
  {
    items: CALC_NUMBER_KEYS,
    keyClass: { gray: true, 'calc-number': true },
  },
];

export const CALC_ITEMS_PER_KEY = {};
for (let i = 0; i < CALC_ROWS.length; i++) {
  const row = CALC_ROWS[i];
  for (let j = 0; j < row.items.length; j++) {
    const it = row.items[j];
    CALC_ITEMS_PER_KEY[it.calculationKey === undefined ? it.displayedString : it.calculationKey] = it;
  }
}

/**
 * Format an expression from a calculation items array so it can be displayed or used in a parser
 * @param  {Array}  calcItems - Array of IndicatorCalcItem
 * @param  {Array}  values - If set values used to replace items of type CALC_FIELD_KEYS
 * @param  {Boolean} [forDisplay=false] - If true, replace calculationKey with displayedString
 * @return {String} - Expression usable for Math parser (forDisplay = false) or for display (forDisplay = true)
 */
export function formattedIndicatorExpression(calcItems, values, forDisplay = false) {
  values = values instanceof Array ? values : [Number(values)];
  let valuesIndex = 0;
  return calcItems.reduce((expr, item) => {
    let exprPart = item.calculationKey;
    // Check calc items properties
    const refItem = CALC_ITEMS_PER_KEY[item.calculationKey];
    // If a param value
    if (exprPart === 'value' || exprPart === 'indicator-value') {
      const val = values.length && values.length > valuesIndex ? values[valuesIndex++] : values[0];
      exprPart = val === null ? 0 : val;
    } else if (forDisplay && (refItem.displayedString || refItem.displayedStringTr)) {
      exprPart = refItem.displayedStringTr ? i18n.t(refItem.displayedStringTr) : refItem.displayedString;
    }
    if (refItem.spaceAround) {
      exprPart = ` ${exprPart} `;
    }
    return `${expr}${exprPart}`;
  }, '');
}

export class ChartPoint {
  paramKeys = [
    'data',
    'pbImpact',
    'comment',
    'comments',
    'objectiveData',
    'tooltip',
    'goodBadNeutral',
    'color',
    'period',
    'dates',
    'missingValues',
  ];

  data = null;

  pbImpact = null;

  comment = '';

  comments = [];

  objectiveData = null;

  tooltip = null;

  goodBadNeutral = -1;

  color = null;

  period = null;

  dates = [];

  missingValues = [];

  constructor(content) {
    if (content) {
      this.paramKeys.forEach((key) => {
        this[key] = content === undefined || content[key] === undefined ? this[key] : content[key];
      });
    }
  }

  isBetween(start, end) {
    return this.dates.some(date => date.isBetween(start, end, undefined, '[]'));
  }

  clone() {
    const params = {};
    this.paramKeys.forEach((key) => {
      params[key] = this[key];
    });
    return new ChartPoint(params);
  }
}


/**
 * Set of functions to create indicators chart data and draw charts
 */
export class ChartHelper {
  // Min and max radius of points illustrating the impacts of problems reported at a date
  // This radius is a percentage of the height of the chart
  maxImpactRadius = 10;

  minImpactRadius = 5;

  impactRadiusRange = this.maxImpactRadius - this.minImpactRadius;

  constructor() {
    this.dateFormat = i18n.t('minDateFormat');
    this.twoDots = i18n.t('message.TWO_DOTS');
  }

  /**
   * Current connected team
   * @return {Object}  Current connected team as an object
   */
  // eslint-disable-next-line class-methods-use-this
  get currentTeam() {
    return store.state.currentTeam;
  }

  // =====
  // Utils
  // =====
  /**
   * Return an integer based on whther the value is OK or NOK
   * @param  {Number} value          Indicator value
   * @param  {Number} objective      Indicator objective
   * @param  {Number} objectiveIsMax Whether the objective is a max or a min
   * @return {Number}                1: good, 0: bad, -1 neutral
   */
  // eslint-disable-next-line class-methods-use-this
  getGoodBadNeutral(value, objective, objectiveIsMax) {
    if (value === null) {
      return -1;
    }
    const valEqObj = value === objective;
    const objIsMin = Number(objectiveIsMax) === 0;
    const valAboveObj = value > objective;
    return valEqObj || objIsMin === valAboveObj ? 1 : 0;
  }

  getColor(value, objective, objectiveIsMax) {
    const which = this.getGoodBadNeutral(value, objective, objectiveIsMax);
    if (which === 1) {
      return SColors.indicatorsGreenLine;
    }
    if (which === 0) {
      return SColors.indicatorsRedLine;
    }
    return SColors.gray;
  }

  /**
   * Convert an array of chart points into radiuses of chart points
   * Radius are rules by a minImpactRadius and maxImpactRadius
   * @param  {Array} impacts - array of objects like { nb: nb_of_problems, impact: sum_of_impacts }
   * @return {Array} - array of numbers
   */
  getRadiusFromImpacts(points = []) {
    const max = Math.max(
      ...points.map(({ pbImpact }) => (pbImpact && pbImpact.resolvedImpact) || 0),
      ...points.map(({ pbImpact }) => (pbImpact && pbImpact.reportedImpact) || 0),
    );
    return points.map(({ pbImpact }) => {
      let reportedRadius = 0;
      let resolvedRadius = 0;
      if (pbImpact && pbImpact.nReported > 0) {
        reportedRadius = max === 0
          ? this.minImpactRadius
          : this.minImpactRadius + this.impactRadiusRange * pbImpact.reportedImpact / max;
      }
      if (pbImpact && pbImpact.nResolved > 0) {
        resolvedRadius = max === 0
          ? this.minImpactRadius
          : this.minImpactRadius + this.impactRadiusRange * pbImpact.resolvedImpact / max;
      }
      return { reportedRadius, resolvedRadius };
    });
  }


  /**
   * Creates a chart using a user-defined configuration
   *
   * @param  {Object} indicator - indicator object (with objective, objectiveIsMax, objectives and unit)
   * @param  {Object} values - Values formatted as { <YYYY-MM-DD>: { id, value, reported }, <YYYY-MM-DD>: ... }
   * @param  {Object} config - Custom display configuration object : {
   *        configStart: start date of the values
   *        pointPeriod: the period of a chart value, number of <pointUnit>
   *        pointUnit: moment-understandable unit of the period
   *        increasingValues: whether the values should increase over a periodof time
   *        increasePeriod: the number of <increasePeriodUnit> during which the values will increase
   *        increasePeriodUnit: moment-understandable unit of the increasePeriod
   *        increasingObjectives: whether objectives should also increase over the increase period
   *      }
   * @param  {Array} workingDays - Array of boolean values where the index is the weekday and
   *                               the boolean is true if the team works that day
   * @param  {Object} visibleRange - Range of values that should be included in the chart.
   *                                 If undefined, all values will be included
   *
   * @return {Object} - { points: array of chart point, max: chart's maximum }
   */
  buildCustomConfigChart(indicator, values, config, workingDays, visibleRange, indicators) {
    // ==================
    // Initial parameters
    // ==================
    workingDays = workingDays || this.currentTeam.workingDays || [true, true, true, true, true, true, true];

    // Only push values if dates are included in visible range
    const vr = visibleRange;
    const useVisibleRange = vr !== undefined
      && (Moment.isMoment(vr.start) && vr.start.isValid())
      && (Moment.isMoment(vr.end) && vr.end.isValid());
    const chartEnd = useVisibleRange ? vr.end : Moment().add(1, 'year');
    const chartStart = useVisibleRange ? vr.start : Moment().subtract(1, 'year');
    // Boolean to compute end-of-period objectives when increasingObjectives = false
    let includeNextPeriodEnd = false;

    config = computeConfigStart(config, chartStart);
    const {
      increasingValues,
      pointPeriod,
      pointUnit,
      pointStartDay,
      increasePeriod,
      increasePeriodUnit,
      increasingObjectives,
    } = config;
    // If there's no increase period, the point period will be the period of the objective
    const objPeriod = increasingValues ? increasePeriod : pointPeriod;
    const objPeriodUnit = increasingValues ? increasePeriodUnit : pointUnit;
    // Values start date
    const startDate = Moment(config.configStart);
    // Start date of the previous period in each loop
    let oldStartDate = Moment(startDate);
    // End date of the point period
    let endDate = Moment(startDate).add(pointPeriod, pointUnit);
    // End date of the objective period (if increaseValues = false, same as above)
    const periodEndDate = Moment(startDate).add(objPeriod, objPeriodUnit);
    if (objPeriodUnit === 'w') {
      periodEndDate.day((pointStartDay || 1) % 7);
    } else if (objPeriodUnit === 'M') {
      periodEndDate.date(pointStartDay || 1);
    }
    const { objectiveIsMax = false, unit = '', inputPeriodUnit = 'd' } = indicator;
    const displayCalcComments = Number(indicator.displayCalcComments) === 1;
    const cUnit = unit === '' ? '' : ` ${unit}`;
    const isDaily = inputPeriodUnit === 'd';
    const isWeekly = inputPeriodUnit === 'w';
    const isMonthly = inputPeriodUnit === 'M';
    const oneDayPerPoint = pointUnit === 'd' && pointPeriod === 1;
    const cumulateIndicators = indicators !== undefined;
    if (!cumulateIndicators || !indicators.length) {
      indicators = [{ ...indicator, inputValues: values }];
    }
    indicators = indicators.map(i => ({
      ...i,
      workingDays: parseWorkingDays(i.workingDays),
      calcItemsPerId: (i.computedCalcItems || []).reduce((all, item) => ({ ...all, [`${item.id}`]: item }), {}),
    }));
    const isAverage = indicators.most(indic => Number(indic.isAverage) === 1);
    const averageAfterCalculation = indicators.most(indic => Number(indic.averageAfterCalculation) === 1);

    // In case these are calculated indicators, set the proper values order for each one
    let nCalcItems = 0;
    const calculatedIndicators = indicators
      .every(indic => indic.computedCalcItems && indic.computedCalcItems.length > 0);
    if (calculatedIndicators) {
      const valueKeys = CALC_FIELD_KEYS.map(k => k.calculationKey);
      indicators.forEach((indic) => {
        indic.valueCalcItemIds = indic.computedCalcItems
          .filter(i => valueKeys.includes(i.calculationKey)).map(i => i.id);
        nCalcItems = Math.max(nCalcItems, indic.valueCalcItemIds.length);
      });
    }
    const { computedCalcItems } = indicators[0];
    // TODO: dataValue => array of values ?

    // =================================================
    // Iterations variables, increase sections variables
    // =================================================
    let increasingDataValue = null;
    let incrDataCalcValues = new Array(nCalcItems).fill(null);
    let increasingObjValue = 0;
    let nPointsInPeriod = 0;
    let dataValue = null; // Cumulated value for each point (if 1 point = 1 day, not really cumulated)
    // eslint-disable-next-line max-len
    let dataCalcValues = new Array(nCalcItems).fill(null); // Same as above in case indicators are calculated using IndicatorValueCalcValue
    let objValue = 0;
    let comment = null; // Merged comments in a point
    let comments = []; // Merged comments in a point
    let nWDaysInPoint = 0; // Nb of working days in a point (for avg objective)
    let nValInPoint = 0; // To calculate average if required
    let workingDaysValue = false; // True if the point period contains a working day
    let workingDaysPeriod = false; // True if the increase section period contains a working day
    let dataPbImpact = { nResolved: 0, nReported: 0, reportedImpact: 0, resolvedImpact: 0 };
    let missingValues = []; // When cumulating the values of one or several indicator(s), can be useful

    // =====================
    // Output data variables
    // =====================
    let max = 0;
    const chartPoints = [];

    /**
     * End of a point period : Push new point's data into related arrays
     * @param  {ChartHelper} self - current instance of ChartHelper
     * @param  {Boolean} [isIncreasePeriod] - if set and true, this is the end of the increase period
     *                                      Else, it's only the end of the point period
     */
    function endOfPeriod(self, isIncreasePeriod) {
      const chartPoint = new ChartPoint();

      // End date of the period which ends
      const pEndDate = startDate.clone().subtract(1, 'd');

      // Is the current period in the visible range
      const periodIncluded = !useVisibleRange
        || (vr.start.isSameOrBefore(pEndDate, 'day') && vr.end.isSameOrAfter(oldStartDate));
      // If !increasingObjectives, increasingObjectives are set at the end of the period
      includeNextPeriodEnd = includeNextPeriodEnd || periodIncluded;

      // End of the point period => set colors and tooltips content
      // And add a new data elt
      increasingDataValue = increasingDataValue === null && dataValue === null
        ? null
        : Number(increasingDataValue) + Number(dataValue);
      increasingObjValue += objValue;
      if (calculatedIndicators) {
        dataCalcValues.forEach((val, i) => {
          incrDataCalcValues[i] = incrDataCalcValues[i] === null && val === null
            ? null
            : Number(incrDataCalcValues[i]) + Number(val);
        });
      }
      // Non working days should be hidden : check if this point contains a working day
      if (periodIncluded && workingDaysValue) {
        nPointsInPeriod++;
        // Push values into datasets arrays (value, objective, problem impacts)
        let realValue;
        let expression;
        let displayedExpression;
        if (calculatedIndicators) {
          try {
            expression = increasingValues
              ? formattedIndicatorExpression(computedCalcItems, incrDataCalcValues)
              : formattedIndicatorExpression(computedCalcItems, dataCalcValues);
            realValue = evaluate(expression);
            if (realValue === Infinity) {
              realValue = null;
            }
            displayedExpression = increasingValues
              ? formattedIndicatorExpression(computedCalcItems, incrDataCalcValues, true)
              : formattedIndicatorExpression(computedCalcItems, dataCalcValues, true);
          } catch (e) {
            realValue = increasingValues ? increasingDataValue : dataValue;
          }
          realValue = isNaN(realValue) ? null : realValue;
          realValue = isAverage && averageAfterCalculation && nValInPoint > 0 && realValue
            ? realValue / nValInPoint
            : realValue;
        } else {
          realValue = increasingValues ? increasingDataValue : dataValue;
          realValue = isAverage && nValInPoint > 0 && realValue ? realValue / nValInPoint : realValue;
        }
        chartPoint.data = realValue;
        if (increasingValues && increasingObjectives) {
          chartPoint.objectiveData = isAverage ? increasingObjValue / nWDaysInPoint : increasingObjValue;
          chartPoint.color = self.getColor(realValue, chartPoint.objectiveData, objectiveIsMax);
          chartPoint.goodBadNeutral = self.getGoodBadNeutral(realValue, chartPoint.objectiveData, objectiveIsMax);
        }
        chartPoint.pbImpact = dataPbImpact;
        max = Math.max(max, realValue);
        if (realValue !== null) {
          // If a point of a chart contains only one day, display only this day's date
          let ttTitle = oldStartDate.isSame(pEndDate)
            ? oldStartDate.format(self.dateFormat)
            : `${oldStartDate.format(self.dateFormat)} - ${pEndDate.format(self.dateFormat)}`;
          const incrementTickVal = dataValue || 0;
          ttTitle += `${self.twoDots}${realValue.sToFixed(2)}${cUnit}`;
          let tooltipPrefix;
          if (increasingValues || (calculatedIndicators && displayedExpression)) {
            tooltipPrefix = `${realValue.sToFixed(2)}${cUnit}`;
            if (increasingValues) {
              tooltipPrefix += ` (${incrementTickVal}${cUnit})`;
            }
            if (calculatedIndicators && displayedExpression) {
              tooltipPrefix += `  (${displayedExpression})`;
            }
          }
          let tooltipBody = tooltipPrefix;
          // TODO: Uncomment to display the number of missing values per team
          // if (missingValues.length > 0) {
          //   tooltipBody += `<div class="chart-tooltip-mising"><div class="v-centerer">\
          //     <svg class="chart-tooltip-icon" viewbox="0 0 24 24"><path d="${warningMedium}
          //     "/></svg> ${i18n.tc('message.N_MISSING_VALUES',
          //     missingValues.length, { nb: missingValues.length })}</div>`;
          //   const mPerTeam = missingValues.reduce((all, val) => {
          //     all[`${val.team}`] = all[`${val.team}`] === undefined ? [val] : [...all[`${val.team}`], val];
          //     return all;
          //   }, {});
          //   tooltipBody += Object.values(mPerTeam).map(vals => `<div>- ${vals[0].teamName} (${vals.length})</div>`);
          //   tooltipBody += '</div>';
          // }
          if (comment !== null) tooltipBody += `<br>${comment}`;
          chartPoint.tooltip = { title: ttTitle, body: tooltipBody, comments };
          chartPoint.tooltip.beforeBody = tooltipPrefix ? [tooltipPrefix] : [];
          chartPoint.tooltip.afterBody = comments;
        } else {
          chartPoint.tooltip = { title: '', body: '' };
        }
        chartPoint.dates.push(Moment(oldStartDate));
        chartPoint.comment = comment;
        chartPoint.comments = comments;
        chartPoint.period = { start: Moment(oldStartDate), end: Moment(pEndDate) };
        chartPoint.missingValues = missingValues;
        chartPoints.push(chartPoint);
        // Current period contains a working day value OR we don't need to hide the working days anyway
        workingDaysPeriod = true;
      }
      // Period reset
      comment = null;
      comments = [];
      oldStartDate = Moment(startDate);
      dataValue = null;
      dataCalcValues = new Array(nCalcItems).fill(null);
      objValue = 0;
      workingDaysValue = false;
      dataPbImpact = { nResolved: 0, nReported: 0, reportedImpact: 0, resolvedImpact: 0 };
      missingValues = [];
      // End of the increase period. If no !increasingValues, end of the one-point period
      if (isIncreasePeriod && includeNextPeriodEnd && workingDaysPeriod) {
        const realObjValue = isAverage ? increasingObjValue / (nWDaysInPoint || 1) : increasingObjValue;
        if (!increasingValues || !increasingObjectives) {
          // If objectives increase with the values, values are added while being cumulated
          // If not, objectives all have the same value which is the top cumulated values of objectives on the period
          // So we need to calculate the objective of the period to cumulate the objective of each point
          // const realObjValue = isAverage ? increasingObjValue / nValInPoint : increasingObjValue;
          const dataLength = chartPoints.length;
          for (let i = 0; i < nPointsInPeriod; i++) {
            const point = chartPoints[dataLength - nPointsInPeriod + i];
            point.objectiveData = realObjValue;
            point.color = self.getColor(point.data, realObjValue, objectiveIsMax);
            point.goodBadNeutral = self.getGoodBadNeutral(point.data, realObjValue, objectiveIsMax);
          }
        }
        max = Math.max(max, realObjValue);
        // Reset working days check
        workingDaysPeriod = false;
        includeNextPeriodEnd = false;
      }
      if (isIncreasePeriod || !increasingValues) {
        // During an increase, keep the number of values to average
        // for the next point until the end of the increase period
        nValInPoint = 0;
        nWDaysInPoint = 0;
      }
    }


    // Loop through all the values one by one. Values are cumulated per point period and then added to the data array
    // Same principle for the increase periods
    // Additionnally, a null value is added to add a space between increase periods
    do {
      if (startDate.isSame(periodEndDate)) {
        // End of the increase section
        periodEndDate.add(objPeriod, objPeriodUnit);
        endDate = Moment(startDate).add(pointPeriod, pointUnit);
        endOfPeriod(this, true);
        if (increasingValues) {
          // Add a space between increase intervals
          chartPoints.push(new ChartPoint({ dates: [Moment(startDate)] }));
        }
        // Reset increase data
        nPointsInPeriod = 0;
        increasingDataValue = null;
        increasingObjValue = 0;
        incrDataCalcValues = new Array(nCalcItems).fill(null);
      }
      if (startDate.isSame(endDate)) {
        // End of the point period
        endDate.add(pointPeriod, pointUnit);
        endOfPeriod(this);
      }
      const dateKey = startDate.format(valueKeyFormat);
      // Value cumulated when cumulating values of multiple teams. (Separate from dataValue in case isAverage is true)
      let cumulatedVal = null;
      let cumulatedObj = 0;
      let nCumulWorkingDays = 0;
      let isAWorkingDay = false;

      // For weekly (or monthly) indics, only the first day of week (month) is considered a working day
      const weeklyWD = isWeekly && startDate.isSame(Moment(startDate).startOf('isoWeek'));
      const monthlyWD = isMonthly && startDate.isSame(Moment(startDate).startOf('month'));
      // eslint-disable-next-line no-loop-func
      indicators.forEach((singleIndicator) => {
        // If any of the indicators to be cumulated use this date as a working day, it is a working day
        const indicWorkingDay = weeklyWD || monthlyWD || isWorkingDay(startDate, singleIndicator.workingDays);
        isAWorkingDay = isAWorkingDay || indicWorkingDay;
        nCumulWorkingDays += indicWorkingDay ? 1 : 0;
        // For each indicators to cumulate, sum values
        const {
          team,
          teamName,
          objective,
          objectives,
          problemsImpacts,
          inputValues,
          calcItemsPerId,
        } = singleIndicator;
        const iObj = objectives ? objectives[dateKey] : objective;
        cumulatedObj += isNaN(iObj) || iObj === null ? Number(objective) || 0 : Number(iObj);
        const iValue = inputValues[dateKey];
        if (iValue) {
          const allComments = [];
          if (calculatedIndicators && iValue.calcValues) {
            singleIndicator.valueCalcItemIds.forEach((itemId, i) => {
              if (iValue.calcValues[`${itemId}`]) {
                const cVal = iValue.calcValues[`${itemId}`];
                dataCalcValues[i] = Number(dataCalcValues[i]) + Number(cVal.value);
                if (displayCalcComments && ![null, undefined].includes(cVal.commentText) && cVal.commentText.length) {
                  const { indicatorName, themeName, teamName: cTeamName } = calcItemsPerId[`${itemId}`];
                  const prefix = `<span class="semibold">${startDate.format(this.dateFormat)}${this.twoDots}`
                    + `${indicatorName} (${themeName} | ${cTeamName})</span>${this.twoDots}${Number(cVal.value)}`;
                  allComments.push({ prefix, comment: cVal.commentText });
                }
              }
              if (isAWorkingDay && (!iValue.calcValues[`${itemId}`] || iValue.calcValues[`${itemId}`].value === null)) {
                missingValues.push({ dateKey, team, teamName });
              }
            });
          } else if (isAWorkingDay && iValue.value === null) {
            missingValues.push({ dateKey, team, teamName });
          }
          cumulatedVal = cumulatedVal === null && iValue.value === null
            ? null
            : Number(cumulatedVal) + Number(iValue.value);
          if (iValue.commentText && iValue.commentText.length && !displayCalcComments) {
            const strTeamDate = indicators.length > 1
              ? `<span class="semibold">${decodeHTML(teamName)}</span> (${startDate.format(this.dateFormat)})`
              : startDate.format(this.dateFormat);
            allComments.push({
              // No need for the comment's date if the point displays a single day
              prefix: !oneDayPerPoint || indicators.length > 1 ? `${strTeamDate}${this.twoDots}` : undefined,
              comment: iValue.commentText,
            });
          }
          if (allComments && allComments.length) {
            comments = [...comments, ...allComments];
          }
        }
        if (problemsImpacts && problemsImpacts[dateKey]) {
          dataPbImpact.nReported += problemsImpacts[dateKey].nReported;
          dataPbImpact.nResolved += problemsImpacts[dateKey].nResolved;
          dataPbImpact.reportedImpact += problemsImpacts[dateKey].reportedImpact;
          dataPbImpact.resolvedImpact += problemsImpacts[dateKey].resolvedImpact;
        }
      });
      if (cumulateIndicators && isAverage) {
        cumulatedVal = cumulatedVal === null ? null : cumulatedVal / (nCumulWorkingDays || 1);
        cumulatedObj = cumulatedObj === null ? null : cumulatedObj / (nCumulWorkingDays || 1);
      }
      dataValue = dataValue === null && cumulatedVal === null ? null : Number(dataValue) + Number(cumulatedVal);
      objValue += cumulatedObj;
      nValInPoint += cumulatedVal === null ? 0 : 1;

      // This is a working day
      workingDaysValue = workingDaysValue || isAWorkingDay;
      // Use the same days as indicators/func.php to check working days
      if (isDaily) {
        nWDaysInPoint += isAWorkingDay ? 1 : 0;
      } else if (isWeekly) {
        nWDaysInPoint += startDate.day() === 1 ? 1 : 0;
      } else {
        nWDaysInPoint += startDate.date() === 1 ? 1 : 0;
      }
    } while (startDate.add(1, 'd').isBefore(chartEnd));
    endOfPeriod(this, true);

    let min;
    // Prevent Infinity if of all values are null
    const minVal = Math.min(...chartPoints.filter(point => point.data !== null).map(point => point.data));
    if (chartPoints.every(point => point.data === null) || (!Number(indicator.adaptativeValuesRange) && minVal >= 0)) {
      min = 0;
    } else {
      min = minVal;
    }
    min += min === 0 ? 0 : (max - min) * -0.1;
    max += (max - min) * 0.4;
    return { points: chartPoints, max: max || 1, min: min || 0 };
  }


  /**
   * Create a Chart.js chart from a pre-calculated set of data
   * @param  {Object} indicator - Indicator whose this chart refers to
   * @param  {Object} allData - Output of buildCustomConfigChart
   * @param  {HTMLElement} element - canvas element to draw the chart
   * @param  {Boolean} showValues - Should values be drawn on the canvas as numbers
   * @param  {Boolean} showDots - Should the cahrt display hoverable dots for each data value
   * @param  {Boolean} showLabels - Should the chart display labels in abscissa
   * @param  {Number} [maxLabelRotation=0] - Maximum allowed rotation of abscissa labels
   * @param  {Boolean} [displayProblems=false] - If true, override the value of
   *                                             currentTeam.displayProblemsInIndicatorCharts
   * @return {Object} - Chart instance
   */
  drawChart(
    indicator,
    allData,
    element,
    showValues,
    showDots,
    showLabels,
    maxLabelRotation = 0,
    displayProblems = false,
  ) {
    const { points, max, min } = allData;
    const objectiveData = points.map(p => p.objectiveData);
    const minLimit = min >= 0 ? 0 : min;
    const chartMin = Number(indicator.adaptativeValuesRange) === 1 ? min : minLimit;

    showValues = typeof showValues === 'undefined' ? this.currentTeam.displayIndicatorValues !== 0 : showValues;
    showLabels = typeof showLabels === 'undefined'
      ? this.currentTeam.displayIndicatorLabels
      : showLabels;

    // =======================================
    // Labels : date / MTWTF / W1 / Jan. / etc.
    const { labels, maxLabelWidth } = this.getLabelsFromChartPoints(points, showLabels);
    // ========
    // Datasets
    const chartData = {
      labels,
      datasets: [{
        fill: getFillOption(indicator, '+1', this.currentTeam),
        tension: 0,
        data: points.map(p => p.data),
        borderColor: SColors.mainTextColor,
        complexBorderColor: getLineColorOption(indicator, 1),
        borderWidth: 2,
        pointBackgroundColor: points.map(p => p.color),
        pointRadius: 0,
        pointHitRadius: 8,
        pointHoverRadius: 0,
        pointHoverBorderWidth: 0,
        pointBorderWidth: 0,
        minBarLength: 3,
        borderRadius: 200,
      },
      {
        type: 'line',
        fill: false,
        tension: 0,
        data: objectiveData,
        borderColor: SColors.mainTextColor,
        borderWidth: 1,
        borderDash: [3, 3],
        pointRadius: 0,
        pointHoverRadius: 0,
      }],
    };

    // Display the problems impacts if needed
    if (displayProblems || this.currentTeam.displayProblemsInIndicatorCharts) {
      const reportedHeight = max / 1.06;
      const resolvedHeight = max / 1.08;
      const radiuses = this.getRadiusFromImpacts(points);
      const reportedRadiuses = radiuses.map(r => r.reportedRadius);
      const resolvedRadiuses = radiuses.map(r => r.resolvedRadius);

      chartData.datasets.push({
        type: 'line',
        fill: false,
        data: Array(points.length).fill(reportedHeight),
        borderWidth: 0,
        borderColor: 'rgba(0, 0, 0, 0)',
        pointBackgroundColor: 'rgba(255, 0, 0, 0.5)',
        pointRadius: reportedRadiuses,
        pointHoverRadius: reportedRadiuses,
      });
      chartData.datasets.push({
        type: 'line',
        fill: false,
        data: Array(points.length).fill(resolvedHeight),
        borderWidth: 0,
        borderColor: 'rgba(0, 0, 0, 0)',
        pointBackgroundColor: 'rgba(0, 255, 0, 0.5)',
        pointRadius: resolvedRadiuses,
        pointHoverRadius: resolvedRadiuses,
      });
    }
    // Bar chart: no border, the background will be the color
    if (Number(indicator.type) === 1) {
      chartData.datasets[0].borderWidth = 0;
      chartData.datasets[0].maxBarThickness = 8;
      chartData.datasets[0].backgroundColor = points.map(p => p.color);
    }

    // Display dots for ach value
    if (showDots) {
      chartData.datasets[0].pointRadius = 3;
      chartData.datasets[0].pointHoverRadius = 7;
    }

    const options = {
      indicatorComments: points.map(p => p.comments),
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          max: max === 0 ? 1 : max,
          min: chartMin,
          ticks: {
            callback: label => (Math.floor(label) === label ? `${label} ${indicator.unit}` : ''),
            color: SColors.gray,
            font: {
              weight: '600',
              size: 11,
            },
            z: 1,
            padding: 3,
          },
          grid: {
            color: SColors.grayBgHover,
            borderDash: [3, 3],
            tickColor: 'rgba(0, 0, 0, 0)',
            borderColor: 'rgba(0, 0, 0, 0)',
          },
        },
        x: {
          ticks: {
            autoSkip: true,
            autoSkipPadding: Math.round(maxLabelWidth) + 2,
            maxRotation: maxLabelRotation,
            color: SColors.gray,
            font: {
              weight: '600',
              size: 11,
            },
          },
          grid: {
            color: 'rgba(0, 0, 0, 0)',
            borderColor: 'rgba(0, 0, 0, 0)',
          },
        },
      },
      layout: { padding: { left: 3, right: 5, top: 0, bottom: 0 } },
      plugins: {
        // Label bubble
        datalabels: {
          display: context => showValues && context.datasetIndex === 0,
          formatter: val => (val ? val.sToFixed(2) : val),
          color: SColors.mainTextColor,
          backgroundColor: SColors.indicatorGray,
          padding: { top: 2, bottom: 2, left: 3, right: 3 },
          borderRadius: 4,
          font: { weight: '700', size: '11' },
          anchor: 'end',
          align: 'end',
          clamp: true,
          offset: 5,
        },
        tooltip: {
          filter: (item) => {
            // Hide tooltip for reported/resolved problems if no problems
            if (item.datasetIndex === 2 || item.datasetIndex === 3) {
              const impactData = points[item.index] && points[item.index].pbImpact;
              return (impactData && impactData.nReported) || (impactData && impactData.nResolved);
            }
            return true;
          },
          callbacks: {
            title: item => (item && item[0] ? points[item[0].dataIndex].tooltip.title : ''),
            label: (item) => {
              if (item.datasetIndex === 2 || item.datasetIndex === 3) {
                // Reported problems impact dataset
                let text = '';
                const impactData = points[item.dataIndex].pbImpact;
                if (impactData && impactData.nReported) {
                  const { nReported: nb, reportedImpact: impact } = impactData;
                  const nProblems = i18n.tc('message.N_PROBLEMS_REPORTED', nb, { nb });
                  text += i18n.t('message.N_RES_REP_PROBLEMS_IMPACT_SUM', { nProblems, impact: impact.sToFixed(1) });
                }
                if (impactData && impactData.nResolved) {
                  text += text !== '' ? '<br>' : '';
                  const { nResolved: nb, resolvedImpact: impact } = impactData;
                  const nProblems = i18n.tc('message.N_PROBLEMS_RESOLVED', nb, { nb });
                  text += i18n.t('message.N_RES_REP_PROBLEMS_IMPACT_SUM', { nProblems, impact: impact.sToFixed(1) });
                }
                return text;
              }
              // Indicator values dataset
              return points[item.dataIndex].tooltip.body;
            },
            beforeBody: item => (item && item[0] ? points[item[0].dataIndex].tooltip.beforeBody : []),
            afterBody: item => (item && item[0] ? points[item[0].dataIndex].tooltip.afterBody : []),
          },
        },
      },
    };

    return new Chart(element.getContext('2d'), {
      plugins: [ChartDataLabels],
      type: Number(indicator.type) === 0 ? 'dualLine' : 'bar',
      data: chartData,
      options,
    });
  }


  /**
   * Create an array of labels to be used by Chart.js according to an array of ChartPoints
   * @param  {Array}  points             Points of the chart
   * @param  {Number} [showLabels=1]     Whether labels should be displayes or not
   * @return {Object}                    Object with a "labels" property and a "maxLabelWidth" property
   */
  getLabelsFromChartPoints(points, showLabels = 1) {
    const periods = points.map(p => p.period);

    showLabels = typeof showLabels === 'undefined'
      ? this.currentTeam.displayIndicatorLabels
      : showLabels;

    // =======================================
    // Labels : date / MTWTF / W1 / Jan. / etc.
    let dispLabels;
    let maxLabelWidth = 0;
    const firstNonNullPeriod = periods.filter(p => p !== null)[0];
    if (!showLabels) {
      dispLabels = Array(points.length).fill('');
    } else if (firstNonNullPeriod) {
      if ([0, 1, true].includes(showLabels)) {
        // Display plain dates
        let singleDay;
        if (periods && periods.length) {
          const { start, end } = firstNonNullPeriod;
          singleDay = start.isSame(end, 'day');
        }
        dispLabels = periods.map((p) => {
          if (p === null) {
            return '';
          }
          const { start, end } = p;
          const text = singleDay
            ? start.format(this.dateFormat)
            : `${start.format(this.dateFormat)} → ${end.format(this.dateFormat)}`;
          maxLabelWidth = Math.max(getTextWidth(text, '12px "Work Sans"'), maxLabelWidth);
          return text;
        });
      } else {
        // Display a 'code' => first letter of the week day or number of week, ...
        let whichCode;
        if (periods && periods.length) {
          const { start, end } = firstNonNullPeriod;
          if (start.isSame(end, 'day')) {
            whichCode = 1;
          } else if (
            start.isSame(Moment(end).startOf('isoWeek'), 'day')
            && end.isSame(Moment(start).endOf('isoWeek'), 'day')
          ) {
            // One point = one week
            whichCode = 2;
          } else if (
            start.isSame(Moment(start).startOf('isoWeek'), 'day')
            && end.isSame(Moment(end).endOf('isoWeek'), 'day')
          ) {
            // One point = multiple weeks
            whichCode = 3;
          } else if (start.isSame(Moment(end).startOf('M'), 'day') && end.isSame(Moment(start).endOf('M'), 'day')) {
            // One point = single month
            whichCode = 4;
          } else if (start.isSame(Moment(start).startOf('M'), 'day') && end.isSame(Moment(end).endOf('M'), 'day')) {
            // One point = multiple month
            whichCode = 5;
          }
        }
        const weekFirstLetter = i18n.t('message.WEEK').substring(0, 1);
        dispLabels = periods.map((p) => {
          if (p === null) {
            return '';
          }
          const { start, end } = p;
          let text = '';
          if (whichCode === 1) {
            text = start.format('ddd').substring(0, 1).toUpperCase();
          } else if (whichCode === 2) {
            text = `${weekFirstLetter}${start.week()}`;
          } else if (whichCode === 3) {
            text = `${weekFirstLetter}${start.week()} → ${weekFirstLetter}${end.week()}`;
          } else if (whichCode === 4) {
            text = `${start.format('MMM')}`;
          } else if (whichCode === 5) {
            text = `${start.format('MMM')} → ${end.format('MMM')}`;
          }
          maxLabelWidth = Math.max(getTextWidth(text, '12px "Work Sans"'), maxLabelWidth);
          return text;
        });
      }
    }
    return { labels: dispLabels, maxLabelWidth };
  }
}

export function formatConfig(config) {
  if (typeof config.pointPeriodObj === 'object' && typeof config.chartPeriodObj === 'object') {
    return {
      ...config,
      pointPeriod: config.pointPeriodObj.period,
      pointUnit: config.pointPeriodObj.unit,
      pointStartDay: config.pointPeriodObj.startDay,
      chartPeriod: config.chartPeriodObj.period,
      chartUnit: config.chartPeriodObj.unit,
    };
  }
  return config;
}

/**
 * User may set the inputPeriod as week or month which mans that instead of entering
 * a new value each, day, they add a new value each week (or month).
 * Thus the indicator values won't be displayed if the display config is daily
 * @return {Boolean} - Whether the indicator chart should be displayed
 */
export function doesIndicatorMatchConfig(configPointUnit, indicator) {
  switch (configPointUnit) {
    case 'd': return !indicator.inputPeriodUnit || indicator.inputPeriodUnit === 'd';
    case 'w': return indicator.inputPeriodUnit !== 'M';
    case 'M': return true;
    default: return false;
  }
}

/**
 * Displayed string of the content of each calculation expression part within indicators settings
 * @param  {Object} item - expression part
 * @param  {Object} data - Any required data
 * @param  {Boolean} withTeam - Display the name of the indicator's team after its name
 * @return {String} - Title of the expression item
 */
export function expressionItemDisplayedString(item, data, withTeam) {
  if (item.calculationKey === 'indicator-value' && typeof item.indicatorName === 'string') {
    if (withTeam) {
      return `${item.indicatorName} (${item.teamName})`;
    }
    return item.indicatorName;
  }
  for (let i = 0; i < CALC_ROWS.length; i++) {
    const row = CALC_ROWS[i];
    for (let j = 0; j < row.items.length; j++) {
      const it = row.items[j];
      if ((it.calculationKey || it.displayedString) === item.calculationKey) {
        return it.displayedStringTr ? i18n.t(it.displayedStringTr) : it.displayedString;
      }
    }
  }
  return '';
}

/**
 * Class of an expression item
 * @param  {Object} item
 * @return {String} CSS class
 */
export function expressionItemClass(item) {
  for (let i = 0; i < CALC_ROWS.length; i++) {
    const row = CALC_ROWS[i];
    for (let j = 0; j < row.items.length; j++) {
      const it = row.items[j];
      if ((it.calculationKey || it.displayedString) === item.calculationKey) {
        return row.keyClass;
      }
    }
  }
  return '';
}

/**
 * Parse a tooltip of a point to return the HTML version of the values and comments
 * @param  {Object} tooltip             'tooltip' property of a point (must contain beforeBody and afterBody)
 * @param  {String} [fallbackValue='']  Value to return if tooltip doesn't work
 * @param  {Boolean} [noPadding=false]  Add a hard style with padding 0px
 * @param  {Boolean} [keepLineBreaks=false]  Keep line breaks. Default behavior of tiptap is to remove them
 * @return {String}                     HTML string
 */
export function parsePointTooltipHtml(tooltip, fallbackValue = '', noPadding = false, keepLineBreaks = false) {
  if (!tooltip) {
    return fallbackValue;
  }
  const { beforeBody, afterBody } = tooltip;
  if (afterBody && afterBody.length) {
    const globalPrefix = beforeBody.join('<br>');
    const noPaddingStr = noPadding ? 'style="padding: 0px"' : '';
    const content = afterBody
      .map(({ prefix, comment }) => {
        if (['', null, undefined].includes(prefix) && ['', null, undefined].includes(comment)) {
          return '';
        }
        const prefixHtml = ['', null, undefined].includes(prefix) ? '' : `<span class="mr5em">${prefix}</span>`;
        let tiptapHtml = comment;
        try {
          // NOTE Prior to v1.7.39, tiptap content was stored in the DB as a JSON object, hence the JSON parsing.
          // From 1.7.39 onwards, HTML is prefered as it makes it easier to work with in the back-end.
          const obj = JSON.parse(comment);
          utilsTiptapEditor.commands.setContent(obj);
          tiptapHtml = utilsTiptapEditor.getHTML();
        } catch (e) {
          tiptapHtml = comment;
        }
        if (keepLineBreaks) {
          tiptapHtml = tiptapHtml.replaceAll('<p></p>', '<p><br/></p>');
        }
        return `<div class="ProseMirror" ${noPaddingStr}>${prefixHtml}${tiptapHtml}</div>`;
      })
      .join('<br>');
    return globalPrefix === '' ? content : `${globalPrefix}<br>${content}`;
  }
  return fallbackValue;
}

/**
 * Create an excel file with 3 sheets containing indicator values
 * First sheet will contain values only, second comments only and the third sheet will contain both
 * @param  {Array} indicators    List of indicators (Objects with a 'points' array)
 */
export function exportIndicatorsToExcel(indicators) {
  const dFormat = i18n.t('dateFormat');
  const rows = [];
  const wb = XLSX.utils.book_new();
  wb.Props = {
    Title: i18n.t('message.INDICATORS'),
    Subject: i18n.t('message.INDICATORS'),
    CreateDate: Moment().format(dFormat),
  };

  // I.   Dates row
  const periods = indicators && indicators.length ? indicators[0].points.map(p => p.period) : [];
  const firstNonNullPeriod = periods.filter(p => p !== null)[0];
  let columns = [];
  if (firstNonNullPeriod && periods && periods.length) {
    const { start, end } = firstNonNullPeriod;
    const singleDay = start.isSame(end, 'day');
    columns = periods.map((p) => {
      if (p === null) {
        return '';
      }
      const { start: pStart, end: pEnd } = p;
      return singleDay ? pStart.format(dFormat) : `${pStart.format(dFormat)} → ${pEnd.format(dFormat)}`;
    });
  }
  rows.push([i18n.t('message.THEME'), i18n.t('message.INDICATOR'), ...columns]);


  // II. First sheet : One row per indicator with each values
  const firstSheetMerges = [];
  const firstSheetRows = [...rows];
  let currentRow = 1;
  let tmpThemeId = 0;
  let startMergeRow = currentRow;
  indicators.forEach((indic, idx) => {
    // TODO themeName du public est pas bon
    const indicRow = [indic.themeName, indic.name, ...indic.points.map(p => (p.data === null ? '' : p.data))];
    // At each new theme, merge the same-theme cells
    if ((tmpThemeId && tmpThemeId !== indic.theme) || idx === indicators.length - 1) {
      const row = idx === indicators.length - 1 ? currentRow : currentRow - 1;
      firstSheetMerges.push({ s: { r: startMergeRow, c: 0 }, e: { r: row, c: 0 } });
      startMergeRow = currentRow;
    }
    tmpThemeId = indic.theme;
    firstSheetRows.push(indicRow);
    currentRow++;
  });
  const firstSheet = XLSX.utils.table_to_sheet(convertAOAToTable(firstSheetRows), { raw: true, type: 'string' });
  firstSheet['!merges'] = firstSheetMerges;
  setColumnSize(firstSheet, 20);
  XLSX.utils.book_append_sheet(wb, firstSheet, i18n.t('message.INDICATORS'));


  // III. Second sheet : One row per indicator for all the comments
  const secondSheetMerges = [...firstSheetMerges];
  const secondSheetRows = [...rows];
  indicators.forEach((indic) => {
    const indicRow = [
      indic.themeName,
      indic.name,
      ...indic.points.map(({ tooltip }) => parsePointTooltipHtml(tooltip, '', false, true)),
    ];
    secondSheetRows.push(indicRow);
  });
  const secondSheet = XLSX.utils.table_to_sheet(convertAOAToTable(secondSheetRows), { raw: true, type: 'string' });
  secondSheet['!merges'] = secondSheetMerges;
  setColumnSize(secondSheet, 20);
  XLSX.utils.book_append_sheet(wb, secondSheet, i18n.t('message.COMMENTS'));


  // IV. Third sheet : Both values and comments
  currentRow = 1;
  tmpThemeId = 0;
  startMergeRow = currentRow;
  const thirdSheetMerges = [];
  const thirdSheetRows = [...rows];
  indicators.forEach((indic, idx) => {
    const indicRow = [indic.themeName, indic.name, ...indic.points.map(p => (p.data === null ? '' : p.data))];
    const secondIndicRow = [
      indic.themeName,
      indic.name,
      ...indic.points.map(({ tooltip }) => parsePointTooltipHtml(tooltip, '', false, true)),
    ];
    thirdSheetRows.push(indicRow);
    thirdSheetRows.push(secondIndicRow);
    // Merge same-indicator rows
    thirdSheetMerges.push({ s: { r: currentRow, c: 1 }, e: { r: currentRow + 1, c: 1 } });
    // Merge same-theme rows
    if ((tmpThemeId && tmpThemeId !== indic.theme) || idx === indicators.length - 1) {
      const row = idx === indicators.length - 1 ? currentRow + 1 : currentRow - 1;
      thirdSheetMerges.push({ s: { r: startMergeRow, c: 0 }, e: { r: row, c: 0 } });
      startMergeRow = currentRow;
    }
    tmpThemeId = indic.theme;
    currentRow += 2;
  });
  const thirdSheet = XLSX.utils.table_to_sheet(convertAOAToTable(thirdSheetRows), { raw: true, type: 'string' });
  thirdSheet['!merges'] = thirdSheetMerges;
  setColumnSize(thirdSheet, 20);
  XLSX.utils.book_append_sheet(wb, thirdSheet, i18n.t('message.INDICATORS_AND_COMMENTS'));


  // V. Download the file
  const filename = `${i18n.t('message.INDICATORS')}_Shizen_${Moment().format(i18n.t('lFullDateFormat'))}.xlsx`;
  const wopts = {
    bookType: 'xlsx',
    bookSST: true,
    type: 'binary',
  };
  XLSX.writeFile(wb, filename, wopts);
}
